import { Button, Menu, MenuItem } from '@mui/material'
import { useState } from 'react'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useLabelingSlice from 'redux/slices/hooks/useLabelingSlice'
import { useAuth } from 'services/auth'
import {
  buildLabelFileName,
  downloadTextFile,
  generateLabelFile,
  LabelFileFormat,
  LabelRow,
} from './labelingFileUtils'

interface LabelExportButtonProps {
  /** Override the run id, e.g. for the session-less Vet-from-file flow. */
  runId?: string
}

/**
 * EXPORT button with a CSV / TSV menu. Downloads only the labeled cells with
 * the schema run_id, cell_id, label, labeler_id, labeled_at.
 */
export const LabelExportButton = ({ runId: runIdOverride }: LabelExportButtonProps = {}): JSX.Element => {
  const { user } = useAuth()
  const {
    cellVisualizations: { sessionData },
  } = useCellVisualizationsSlice()
  const {
    labeling: { labels, labeledAt },
    markExported,
  } = useLabelingSlice()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const labeledCellIds = Object.keys(labels)
  const hasLabels = labeledCellIds.length > 0
  // The session is single-run (enforced by labeling eligibility), so every row
  // shares this run id.
  const runId =
    runIdOverride ?? sessionData?.runs?.[0]?.run_id ?? String(sessionData?.session_id ?? 'session')
  const labelerId = user?.email ?? ''

  const handleExport = (format: LabelFileFormat) => {
    const rows: LabelRow[] = labeledCellIds.map((cellId) => ({
      run_id: runId,
      cell_id: cellId,
      label: labels[cellId],
      labeler_id: labelerId,
      labeled_at: labeledAt[cellId] ?? '',
    }))
    const isoDate = new Date().toISOString().slice(0, 10)
    downloadTextFile(buildLabelFileName(runId, format, isoDate), generateLabelFile(rows, format))
    markExported()
    setAnchorEl(null)
  }

  return (
    <>
      <Button
        size="small"
        variant="contained"
        disabled={!hasLabels}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid="export-button"
      >
        Export
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => handleExport('csv')}>Export CSV</MenuItem>
        <MenuItem onClick={() => handleExport('tsv')}>Export TSV</MenuItem>
      </Menu>
    </>
  )
}

export default LabelExportButton
